import {
  Box,
  Flex,
  HStack,
  Image,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as InteractivePDFIcon } from 'assets/icons/interactive-pdf-info.svg';
import pngHDImg from 'assets/images/pngHD_download.png';
import { DownloadOption } from 'constants/download';
import { DOWNLOAD_MODAL_HELP_LINK, PPT_DOWNLOAD_HELP_LINK } from 'constants/links';
import { HD_EXPORT_BREAKPOINT } from 'modules/Editor/components/Toolbar/PageMenu/EditPageSize/config';
import { format } from 'modules/Editor/components/Toolbar/PageMenu/EditPageSize/helpers';
import { usePageSizeControl } from 'modules/Editor/components/Toolbar/PageMenu/EditPageSize/hooks';
import { useAppSelector } from 'modules/Editor/store';
import { selectPageCount } from 'modules/Editor/store/infographSelector';
import { InputError } from 'modules/common/components/Input';
import { RadioOption } from 'modules/common/components/Radio/RadioOption';
import { RadioOptionBox } from 'modules/common/components/Radio/RadioOptionBox';
import { Toggletip } from 'modules/common/components/Toggletip';
import { useTranslation } from 'react-i18next';

import { DownloadOptionProps } from './DownloadModal.types';
import { DownloadOptionWrapper } from './DownloadOptionWrapper';
import { OptionTitle } from './OptionTitle';

const pdfInfoTestId = 'pdf-info-iconBtn';
const exportErrorId = 'download-export-error';

export const DownloadOptionSelect = ({
  downloadOption,
  handleDownloadOption,
  gatePNGExport,
  gatePDFExport,
  gateHTMLExport,
  handleClickUpgradePremium,
  handleClickUpgradeBusiness,
}: DownloadOptionProps) => {
  const { t } = useTranslation(['common_navbar', 'editor_toolbar_page_menu']);
  const { hasExceededHDExport, unit } = usePageSizeControl();

  const exportError = t('editor_toolbar_page_menu:pageSize.exceedHdExportTitle', {
    maxPixel: format(HD_EXPORT_BREAKPOINT, unit),
    unit,
  });

  const titleMarginBottom = gatePNGExport ? '4' : '1';
  const optionStackSpacing = gatePNGExport ? '2' : '0';
  const pageCount = useAppSelector(selectPageCount);
  const hasMultiplePages = pageCount > 1;
  const isHTMLDisabled = hasMultiplePages || gateHTMLExport;

  const handleClickRadio = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    // Find radio input
    const radioInputEl = e.currentTarget.getElementsByTagName('input');
    const [option] = radioInputEl;
    const { disabled } = radioInputEl[0];
    if (option?.value && !disabled) {
      handleDownloadOption?.(option?.value);
    }
  };

  return (
    <>
      {/* Title */}
      <Text fontSize='sm' marginTop={2} fontWeight='semibold' lineHeight='1.2' mb={titleMarginBottom}>
        {t('common_navbar:modals.download.choose-format')}
      </Text>
      {hasExceededHDExport && !gatePNGExport && <InputError id={exportErrorId}>{exportError}</InputError>}
      {/* RadioGroupBox */}
      <RadioGroup w='full' value={gatePNGExport ? 0 : downloadOption}>
        <Stack spacing={optionStackSpacing}>
          <DownloadOptionWrapper condition={gatePNGExport}>
            {gatePNGExport && (
              <OptionTitle
                upgradeDescription={t('common_navbar:modals.publish-panel.upgrade')}
                shouldUpgrade={gatePNGExport}
                handleClickUpgrade={handleClickUpgradePremium}
                postText={t('common_navbar:modals.download.to-download')}
              ></OptionTitle>
            )}
            <RadioOptionBox
              isDisabled={gatePNGExport}
              selected={DownloadOption.PNG === downloadOption}
              onClick={handleClickRadio}
            >
              <Box>
                <RadioOption value={DownloadOption.PNG} isDisabled={gatePNGExport} borderColor={'gray.500'}>
                  {t('common_navbar:modals.download.option-png')}
                  <Text as='span' fontSize='xs' fontWeight='normal' noOfLines={1} color={'privateLinkView.footerBg'}>
                    {t('common_navbar:modals.download.option-png-description')}
                  </Text>
                </RadioOption>
              </Box>
            </RadioOptionBox>
            <RadioOptionBox
              isDisabled={gatePNGExport}
              selected={DownloadOption.PNGHD === downloadOption}
              onClick={handleClickRadio}
            >
              <Box>
                <RadioOption
                  value={DownloadOption.PNGHD}
                  isDisabled={hasExceededHDExport || gatePNGExport}
                  borderColor={'gray.500'}
                  aria-describedby={exportErrorId}
                >
                  {t('common_navbar:modals.download.option-png-hd')}
                  <Image
                    src={pngHDImg}
                    w='6'
                    h='5'
                    p='1'
                    display='inline'
                    verticalAlign='top'
                    alt='png HD download image'
                  />
                  <Text as='span' fontSize='xs' fontWeight='normal' noOfLines={1}>
                    {t('common_navbar:modals.download.option-png-hd-description')}
                  </Text>
                </RadioOption>
              </Box>
            </RadioOptionBox>
          </DownloadOptionWrapper>
          <DownloadOptionWrapper condition={gatePDFExport}>
            {gatePDFExport && (
              <OptionTitle
                upgradeDescription={t('common_navbar:modals.download.upgrade-business')}
                shouldUpgrade={gatePDFExport}
                handleClickUpgrade={handleClickUpgradeBusiness}
                postText={t('common_navbar:modals.download.to-download-pdf')}
              ></OptionTitle>
            )}
            <RadioOptionBox
              isDisabled={gatePDFExport}
              selected={DownloadOption.InteractivePDF === downloadOption}
              onClick={handleClickRadio}
            >
              <RadioOption
                value={DownloadOption.InteractivePDF}
                isDisabled={gatePDFExport}
                borderColor={'gray.500'}
                skipTextWrap
              >
                <HStack align='center' spacing='0.5'>
                  <Text fontSize='sm' fontWeight='medium'>
                    {t('common_navbar:modals.download.option-interactive-pdf')}
                  </Text>
                  {!gatePDFExport && (
                    <Toggletip
                      hasArrow={true}
                      placement={'top'}
                      offset={[60, 6]}
                      icon={<InteractivePDFIcon data-testid={pdfInfoTestId} />}
                      buttonAriaLabel={'pdf information tooltip'}
                      contentStyle={{ width: 'fit-content', borderRadius: 'base' }}
                    >
                      <PDFInfoToolTip />
                    </Toggletip>
                  )}
                </HStack>
                <Text fontSize='xs'>{t('common_navbar:modals.download.option-interactive-pdf-description')}</Text>
              </RadioOption>
            </RadioOptionBox>
            <Popover trigger='hover' placement='right'>
              <PopoverTrigger>
                <Box>
                  <RadioOptionBox
                    isDisabled={gatePDFExport}
                    selected={DownloadOption.PowerPoint === downloadOption}
                    onClick={handleClickRadio}
                  >
                    <RadioOption
                      value={DownloadOption.PowerPoint}
                      isDisabled={gatePDFExport}
                      skipTextWrap
                      borderColor={'gray.500'}
                    >
                      <Text fontSize='sm' fontWeight='medium'>
                        {t('common_navbar:modals.download.option-ppt')}
                      </Text>
                      <Text fontSize='xs'>{t('common_navbar:modals.download.option-ppt-description')}</Text>
                    </RadioOption>
                  </RadioOptionBox>
                </Box>
              </PopoverTrigger>
              {!gatePDFExport && (
                <PopoverContent
                  w='fit-content'
                  bg='font.500'
                  color='white'
                  borderColor='gray.500'
                  textAlign='center'
                  borderRadius='base'
                >
                  <PopoverArrow bg='font.500' />
                  <PopoverBody>
                    <PPTInfoToolTip />
                  </PopoverBody>
                </PopoverContent>
              )}
            </Popover>
            <Popover trigger='hover' placement='right' isLazy>
              <PopoverTrigger>
                <Box>
                  <RadioOptionBox
                    selected={DownloadOption.HTML === downloadOption}
                    isDisabled={isHTMLDisabled}
                    onClick={handleClickRadio}
                  >
                    <Box>
                      <RadioOption value={DownloadOption.HTML} isDisabled={isHTMLDisabled} borderColor={'gray.500'}>
                        {t('common_navbar:modals.download.option-html')}
                        <Text
                          as='span'
                          fontSize='xs'
                          fontWeight='normal'
                          noOfLines={1}
                          color={'privateLinkView.footerBg'}
                        >
                          {t('common_navbar:modals.download.option-html-description')}
                        </Text>
                      </RadioOption>
                    </Box>
                  </RadioOptionBox>
                </Box>
              </PopoverTrigger>
              {hasMultiplePages && (
                <PopoverContent
                  w='fit-content'
                  bg='font.500'
                  color='white'
                  borderColor='gray.500'
                  textAlign='center'
                  borderRadius='base'
                >
                  <PopoverArrow bg='font.500' />
                  <PopoverBody>
                    <Text as='span' fontSize='xs' align='left'>
                      {t('common_navbar:modals.download.tooltip-text-html')}
                    </Text>
                  </PopoverBody>
                </PopoverContent>
              )}
            </Popover>
          </DownloadOptionWrapper>
        </Stack>
      </RadioGroup>
    </>
  );
};

const PDFInfoToolTip = () => {
  const { t } = useTranslation('common_navbar');
  const helpLinkTestId = 'a11y-help-link';

  return (
    <Flex flexDir='column' w='60' alignItems='flex-start'>
      <Text as='span' mb='2' fontSize='xs' align='left'>
        {t('modals.download.tooltip-text-pdf')}
      </Text>
      <Link
        w='fit-content'
        href={DOWNLOAD_MODAL_HELP_LINK}
        isExternal
        variant='external'
        fontSize='xs'
        color={'upgrade.blue.300'}
        fontWeight={'semibold'}
        data-testid={helpLinkTestId}
      >
        {t('modals.download.accessibility-prompt-link')}
      </Link>
    </Flex>
  );
};

const PPTInfoToolTip = () => {
  const { t } = useTranslation('common_navbar');
  const pptInfoLinkTestId = 'ppt-info-link';

  return (
    <Flex flexDir='column' w='52' alignItems='flex-start' pl='1'>
      <Text as='span' mb='2' fontSize='xs' align='left'>
        {t('modals.download.tooltip-text-ppt')}
      </Text>
      <Link
        w='fit-content'
        href={PPT_DOWNLOAD_HELP_LINK}
        isExternal
        variant='external'
        fontSize='xs'
        fontWeight={'semibold'}
        color={'upgrade.blue.300'}
        data-testid={pptInfoLinkTestId}
      >
        {t('modals.download.ppt-info-prompt-link')}
      </Link>
    </Flex>
  );
};
